import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAdoption } from '../context/AdoptionContext';
import { usePractice } from '../context/PracticeContext';
import api from '../services/api';
import '../styles/NewtonAI.css';

// Newton avatar — apple with neural network motif
const NEWTON_AVATAR = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
  <defs>
    <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#4f46e5"/>
      <stop offset="100%" stop-color="#7c3aed"/>
    </linearGradient>
  </defs>
  <rect width="64" height="64" rx="16" fill="url(#bg)"/>
  <g transform="translate(32,33)" fill="none" stroke="white" stroke-width="1.6" stroke-linecap="round">
    <ellipse rx="13" ry="15" stroke-width="2"/>
    <path d="M0,-15 Q4,-22 2,-26" stroke-width="2"/>
    <circle cx="0" cy="-2" r="2.5" fill="white" stroke="none"/>
    <circle cx="-6" cy="-7" r="1.8" fill="white" stroke="none"/>
    <circle cx="6" cy="-7" r="1.8" fill="white" stroke="none"/>
    <circle cx="-5" cy="5" r="1.8" fill="white" stroke="none"/>
    <circle cx="5" cy="5" r="1.8" fill="white" stroke="none"/>
    <line x1="0" y1="-2" x2="-6" y2="-7" opacity="0.6"/>
    <line x1="0" y1="-2" x2="6" y2="-7" opacity="0.6"/>
    <line x1="0" y1="-2" x2="-5" y2="5" opacity="0.6"/>
    <line x1="0" y1="-2" x2="5" y2="5" opacity="0.6"/>
    <line x1="-6" y1="-7" x2="-5" y2="5" opacity="0.4"/>
    <line x1="6" y1="-7" x2="5" y2="5" opacity="0.4"/>
  </g>
</svg>`)}`;

function NewtonAI() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [expanded, setExpanded] = useState(() => localStorage.getItem('newton-ai-expanded') === '1');
  const [tab, setTab] = useState('chat'); // 'chat' | 'help' | 'started'
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { pageContent, startWalkthrough, recordEvent } = useAdoption();
  // Super-admins don't have an activePracticeId on their JWT — they pick a
  // context via the Practice switcher. Pass that selection to the AI ask
  // endpoint so it can pull live practice data even when the user is in
  // super-admin mode. (Prod log showed `practiceId: null | intents: []`
  // because activePracticeId was null on these users.)
  const { selectedPracticeId } = usePractice();

  // Drag state for repositioning the widget
  const [position, setPosition] = useState({ bottom: 24, right: 24 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, startBottom: 0, startRight: 0, moved: false });
  const fabRef = useRef(null);

  const handleDragStart = useCallback((e) => {
    // Only allow drag from the FAB itself, ignore if panel is open
    if (open) return;
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    dragRef.current = {
      dragging: true,
      startX: clientX,
      startY: clientY,
      startBottom: position.bottom,
      startRight: position.right,
      moved: false,
    };
    e.preventDefault();
  }, [open, position]);

  useEffect(() => {
    const handleDragMove = (e) => {
      if (!dragRef.current.dragging) return;
      const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
      const dx = clientX - dragRef.current.startX;
      const dy = clientY - dragRef.current.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        dragRef.current.moved = true;
      }
      const newRight = Math.max(8, Math.min(window.innerWidth - 60, dragRef.current.startRight - dx));
      const newBottom = Math.max(8, Math.min(window.innerHeight - 60, dragRef.current.startBottom + dy));
      setPosition({ bottom: newBottom, right: newRight });
    };
    const handleDragEnd = () => {
      dragRef.current.dragging = false;
    };
    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('touchmove', handleDragMove, { passive: false });
    window.addEventListener('touchend', handleDragEnd);
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, []);

  const handleFabClick = useCallback(() => {
    // If the user dragged, don't toggle — it was a reposition
    if (dragRef.current.moved) {
      dragRef.current.moved = false;
      return;
    }
    if (minimized) {
      setMinimized(false);
      return;
    }
    setOpen(prev => !prev);
  }, [minimized]);

  // Help tab state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const messagesContainerRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (open && tab === 'chat' && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open, tab]);

  // Persist chat scroll position across open/close so the user doesn't lose
  // their place when they minimize the widget (testers flagged "scroll
  // position is lost after exit" as a Low-severity UX issue).
  useEffect(() => {
    if (!open || tab !== 'chat') return;
    const container = messagesContainerRef.current;
    if (!container) return;
    const stored = parseInt(localStorage.getItem('newton-ai-chat-scroll') || '', 10);
    if (Number.isFinite(stored) && stored > 0) {
      // Restore on next frame so the messages have laid out
      requestAnimationFrame(() => {
        if (messagesContainerRef.current) messagesContainerRef.current.scrollTop = stored;
      });
    }
    const onScroll = () => {
      localStorage.setItem('newton-ai-chat-scroll', String(container.scrollTop));
    };
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [open, tab]);

  // === AI Chat handlers ===
  const handleAsk = useCallback(async () => {
    if (!input.trim() || loading) return;
    const query = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: query }]);
    setLoading(true);
    try {
      const currentPage = location.pathname.replace('/practice-view/', '').replace('/admin/', '') || 'dashboard';
      // Carry the last 6 turns of this chat so follow-ups ("is the intake
      // done for this appt") resolve "this" against the patient we were
      // just discussing instead of being treated as a fresh question.
      const history = messages
        .filter(m => m && typeof m.text === 'string' && (m.role === 'user' || m.role === 'assistant'))
        .slice(-6)
        .map(m => ({ role: m.role, text: m.text }));
      const res = await api.askAiAssistant(query, currentPage, selectedPracticeId, history);
      if (res.success) {
        setMessages(prev => [...prev, {
          role: 'assistant', text: res.data.answer, id: res.data.id,
          suggestedArticles: res.data.suggestedArticles,
          suggestedWalkthroughs: res.data.suggestedWalkthroughs,
          deepLinks: res.data.deepLinks, latencyMs: res.data.latencyMs,
        }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I could not process your question. Please try again.' }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Something went wrong. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, location.pathname, selectedPracticeId, messages]);

  const handleFeedback = useCallback(async (queryId, feedback) => {
    try {
      await api.submitAiAssistantFeedback(queryId, feedback);
      setMessages(prev => prev.map(m => m.id === queryId ? { ...m, feedbackGiven: feedback } : m));
    } catch {}
  }, []);

  const handleDeepLink = useCallback((path) => {
    navigate(path);
    setOpen(false);
  }, [navigate]);

  const handleStartWalkthrough = useCallback((wt) => {
    startWalkthrough(wt);
    setOpen(false);
  }, [startWalkthrough]);

  const handleWtFromAi = useCallback((wtId) => {
    const allWts = pageContent?.allWalkthroughs || pageContent?.walkthroughs || [];
    const found = allWts.find(w => w.id === wtId);
    if (found) { startWalkthrough(found); setOpen(false); }
  }, [pageContent, startWalkthrough]);

  // === Help tab handlers ===
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    recordEvent('search_performed', null, null, { query: searchQuery });
    try {
      const res = await api.searchAdoptionKnowledge(searchQuery);
      if (res.success) setSearchResults(res.data || []);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  }, [searchQuery, recordEvent]);

  const handleArticleClick = useCallback(async (article) => {
    recordEvent('article_viewed', 'knowledge_article', article.id);
    if (!article.content_markdown) {
      try {
        const res = await api.getAdoptionArticle(article.slug || article.id);
        if (res.success) { setSelectedArticle(res.data); return; }
      } catch {}
    }
    setSelectedArticle(article);
  }, [recordEvent]);

  const renderMarkdown = (md) => {
    if (!md) return '';
    const escaped = md
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    // Group consecutive bullet/number lines into a single <ul>/<ol> so AI
    // responses like "- Amay212 ...\n- C831 X831 ..." render as a real list
    // instead of literal dashes on each line.
    const lines = escaped.split('\n');
    const out = [];
    let listType = null; // 'ul' | 'ol' | null
    const flushList = () => {
      if (listType) { out.push(`</${listType}>`); listType = null; }
    };
    for (const raw of lines) {
      const ul = raw.match(/^\s*[-*]\s+(.*)$/);
      const ol = raw.match(/^\s*\d+\.\s+(.*)$/);
      if (ul) {
        if (listType !== 'ul') { flushList(); out.push('<ul>'); listType = 'ul'; }
        out.push(`<li>${ul[1]}</li>`);
      } else if (ol) {
        if (listType !== 'ol') { flushList(); out.push('<ol>'); listType = 'ol'; }
        out.push(`<li>${ol[1]}</li>`);
      } else {
        flushList();
        out.push(raw);
      }
    }
    flushList();

    return out.join('\n')
      .replace(/^### (.+)$/gm, '<h4>$1</h4>')
      .replace(/^## (.+)$/gm, '<h3>$1</h3>')
      .replace(/^# (.+)$/gm, '<h2>$1</h2>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code>$1</code>')
      // Don't insert <br/> right after a block-level close tag — collapses
      // the visual gap that <ul></ul> already provides.
      .replace(/(<\/(?:ul|ol|h[1-6])>)\n/g, '$1')
      .replace(/\n(?!<(?:ul|ol|li|h[1-6]))/g, '<br/>');
  };

  const toggleExpanded = useCallback(() => {
    setExpanded(prev => {
      const next = !prev;
      localStorage.setItem('newton-ai-expanded', next ? '1' : '0');
      return next;
    });
  }, []);

  // Data from adoption context
  const walkthroughs = pageContent?.walkthroughs || [];
  const allWalkthroughs = pageContent?.allWalkthroughs || [];
  const completedIds = pageContent?.completedWalkthroughIds || [];
  const articles = pageContent?.articles || [];

  const suggestions = [
    "What are tomorrow's appointments?",
    'How many patients do we have?',
    'Show me pending intake forms',
    'How do I send a campaign?',
    'Who has unread messages?',
  ];

  // Compute panel position so it stays within viewport
  const panelBottom = position.bottom + 64;
  const panelRight = position.right;

  return (
    <>
      {/* Floating Newton button */}
      <button
        ref={fabRef}
        className={`newton-ai-fab ${open ? 'newton-ai-fab--open' : ''} ${minimized ? 'newton-ai-fab--minimized' : ''}`}
        onClick={handleFabClick}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        style={{ bottom: position.bottom, right: position.right }}
        title={minimized ? 'Open Newton' : 'Newton'}
      >
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <img src={NEWTON_AVATAR} alt="Newton" className="newton-ai-fab-img" />
        )}
        {!open && !minimized && <span className="newton-ai-fab-label">Newton</span>}
      </button>

      {/* Panel */}
      {open && (
        <div className={`newton-ai-panel ${expanded ? 'newton-ai-panel--expanded' : ''}`} style={{ bottom: panelBottom, right: panelRight }}>
          {/* Header */}
          <div className="newton-ai-header">
            <div className="newton-ai-header-left">
              <img src={NEWTON_AVATAR} alt="Newton" className="newton-ai-avatar-img" />
              <div>
                <div className="newton-ai-name">Newton</div>
                <div className="newton-ai-status">Your practice assistant</div>
              </div>
            </div>
            {messages.length > 0 && tab === 'chat' && (
              <button className="newton-ai-new-chat" onClick={() => setMessages([])} title="New conversation">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            )}
            <button className="newton-ai-expand" onClick={toggleExpanded} title={expanded ? 'Shrink' : 'Expand'}>
              {expanded ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 14 10 14 10 20" />
                  <polyline points="20 10 14 10 14 4" />
                  <line x1="14" y1="10" x2="21" y2="3" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              )}
            </button>
            <button className="newton-ai-minimize" onClick={() => { setOpen(false); setMinimized(true); }} title="Minimize">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            <button className="newton-ai-close" onClick={() => setOpen(false)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="newton-ai-tabs">
            <button className={`newton-ai-tab ${tab === 'chat' ? 'active' : ''}`} onClick={() => { setTab('chat'); setSelectedArticle(null); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L15 9L22 12L15 15L12 22L9 15L2 12L9 9Z"/></svg>
              Ask
            </button>
            <button className={`newton-ai-tab ${tab === 'help' ? 'active' : ''}`} onClick={() => { setTab('help'); setSelectedArticle(null); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              Help
            </button>
            <button className={`newton-ai-tab ${tab === 'started' ? 'active' : ''}`} onClick={() => { setTab('started'); setSelectedArticle(null); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
              Get Started
            </button>
          </div>

          {/* Body */}
          <div className="newton-ai-body">

            {/* === CHAT TAB === */}
            {tab === 'chat' && (
              <>
                <div className="newton-ai-messages" ref={messagesContainerRef}>
                  {messages.length === 0 && (
                    <div className="newton-ai-welcome">
                      <img src={NEWTON_AVATAR} alt="Newton" className="newton-ai-welcome-avatar" />
                      <h3>Hi, I'm Newton</h3>
                      <p>Ask me about your practice data, navigate the platform, or get help with any feature.</p>
                      <div className="newton-ai-chips">
                        {suggestions.map((s, i) => (
                          <button key={i} className="newton-ai-chip" onClick={() => { setInput(s); setTimeout(() => inputRef.current?.focus(), 50); }}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {messages.map((msg, i) => (
                    <div key={i} className="newton-ai-msg-group">
                      <div className={`newton-ai-msg ${msg.role}`}>
                        {msg.role === 'assistant' && (
                          <img src={NEWTON_AVATAR} alt="N" className="newton-ai-msg-avatar-img" />
                        )}
                        {msg.role === 'assistant' ? (
                          <div className="newton-ai-msg-bubble newton-ai-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }} />
                        ) : (
                          <div className="newton-ai-msg-bubble">{msg.text}</div>
                        )}
                      </div>

                      {msg.role === 'assistant' && msg.deepLinks?.length > 0 && (
                        <div className="newton-ai-actions">
                          {msg.deepLinks.map((link, j) => (
                            <button key={j} className="newton-ai-action-btn" onClick={() => handleDeepLink(link.path)}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                                <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                              </svg>
                              {link.label}
                            </button>
                          ))}
                        </div>
                      )}

                      {msg.role === 'assistant' && msg.suggestedWalkthroughs?.length > 0 && (
                        <div className="newton-ai-actions">
                          {msg.suggestedWalkthroughs.map((wt, j) => (
                            <button key={j} className="newton-ai-action-btn newton-ai-action-btn--wt" onClick={() => handleWtFromAi(wt.id)}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="5 3 19 12 5 21 5 3" />
                              </svg>
                              {wt.title}
                            </button>
                          ))}
                        </div>
                      )}

                      {msg.role === 'assistant' && msg.id && !msg.feedbackGiven && (
                        <div className="newton-ai-feedback">
                          <button onClick={() => handleFeedback(msg.id, 'helpful')} title="Helpful">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z" />
                              <path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" />
                            </svg>
                          </button>
                          <button onClick={() => handleFeedback(msg.id, 'not_helpful')} title="Not helpful">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M10 15V19a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10z" />
                              <path d="M17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17" />
                            </svg>
                          </button>
                          {msg.latencyMs && <span className="newton-ai-latency">{(msg.latencyMs / 1000).toFixed(1)}s</span>}
                        </div>
                      )}
                      {msg.feedbackGiven && (
                        <div className="newton-ai-feedback-thanks">Thanks for your feedback</div>
                      )}
                    </div>
                  ))}

                  {loading && (
                    <div className="newton-ai-msg-group">
                      <div className="newton-ai-msg assistant">
                        <img src={NEWTON_AVATAR} alt="N" className="newton-ai-msg-avatar-img" />
                        <div className="newton-ai-msg-bubble">
                          <div className="newton-ai-typing"><span /><span /><span /></div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="newton-ai-input">
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Ask Newton anything..."
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAsk()}
                  />
                  <button onClick={handleAsk} disabled={loading || !input.trim()} className="newton-ai-send">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button>
                </div>
              </>
            )}

            {/* === HELP TAB === */}
            {tab === 'help' && (
              <div className="newton-ai-help">
                {selectedArticle ? (
                  <div className="newton-help-article-detail">
                    <button className="newton-help-back" onClick={() => setSelectedArticle(null)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                      Back
                    </button>
                    <h3>{selectedArticle.title}</h3>
                    <div className="newton-help-article-body" dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedArticle.content_markdown || selectedArticle.summary) }} />
                  </div>
                ) : (
                  <>
                    {/* Search */}
                    <div className="newton-help-search">
                      <input
                        type="text" placeholder="Search help articles..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                      />
                      <button onClick={handleSearch} disabled={searching}>
                        {searching ? '...' : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        )}
                      </button>
                    </div>

                    {/* Search results */}
                    {searchResults.length > 0 && (
                      <div className="newton-help-section">
                        <h4>Search Results</h4>
                        {searchResults.map(r => (
                          <div key={r.id} className="newton-help-card" onClick={() => handleArticleClick(r)}>
                            <div className="newton-help-card-title">{r.title}</div>
                            {r.summary && <div className="newton-help-card-desc">{r.summary}</div>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Page walkthroughs */}
                    {walkthroughs.length > 0 && (
                      <div className="newton-help-section">
                        <h4>For This Page</h4>
                        {walkthroughs.map(wt => (
                          <div key={wt.id} className="newton-help-card newton-help-card--wt" onClick={() => handleStartWalkthrough(wt)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                            <div>
                              <div className="newton-help-card-title">{wt.title}</div>
                              {wt.description && <div className="newton-help-card-desc">{wt.description}</div>}
                              <div className="newton-help-card-meta">{(wt.steps || []).length} steps</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Articles for this page */}
                    {articles.length > 0 && (
                      <div className="newton-help-section">
                        <h4>Related Articles</h4>
                        {articles.map(a => (
                          <div key={a.id} className="newton-help-card" onClick={() => handleArticleClick(a)}>
                            <div className="newton-help-card-title">{a.title}</div>
                            {a.summary && <div className="newton-help-card-desc">{a.summary}</div>}
                          </div>
                        ))}
                      </div>
                    )}

                    {walkthroughs.length === 0 && articles.length === 0 && searchResults.length === 0 && (
                      <div className="newton-help-empty">No help content available for this page yet. Try searching above.</div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* === GET STARTED TAB === */}
            {tab === 'started' && (
              <div className="newton-ai-help">
                <div className="newton-help-section">
                  <h4>Getting Started</h4>
                  <p className="newton-help-progress">
                    {completedIds.length} of {allWalkthroughs.length} completed
                  </p>
                  {allWalkthroughs.length === 0 ? (
                    <div className="newton-help-empty">No getting started items available.</div>
                  ) : (
                    allWalkthroughs.map(wt => {
                      const done = completedIds.includes(wt.id);
                      return (
                        <div
                          key={wt.id}
                          className={`newton-checklist-item ${done ? 'done' : ''}`}
                          onClick={() => handleStartWalkthrough(wt)}
                          title={done ? 'Replay this tour' : `${(wt.steps || []).length} steps`}
                        >
                          <span className="newton-check-icon">{done ? '\u2713' : '\u25CB'}</span>
                          <span className="newton-check-label">{wt.title}</span>
                          <span className="newton-check-steps">
                            {done ? 'Replay' : `${(wt.steps || []).length} steps`}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default NewtonAI;
